import { useCallback, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { AppIcon } from '@backrs_ui/common-ui';

import { appStrings as commonAppStrings } from '@backrs_ui/common-ui/src/assets/appStrings';
import { ReactComponent as BackgroundCircles } from '@backrs_ui/common-ui/src/assets/images/background-circles.svg';
import { FeaturedIcon } from '@backrs_ui/common-ui/src/designSystem/icons/FeaturedIcon';
import {
  type AudioRecorderErrorReason,
  useAudioRecorder,
} from '@backrs_ui/common-ui/src/hooks/useAudioRecorder';
import { AttachedFileChip } from '@backrs_ui/common-ui/src/molecules/Lubav/AttachedFileChip';
import { APP_ROUTE } from '@backrs_ui/grow-ui/src/appRoutes.constants';
import { appStrings } from '@backrs_ui/grow-ui/src/assets/appStrings';
import {
  RichTextEditor,
  type RichTextEditorHandle,
} from '@backrs_ui/grow-ui/src/components/RichTextEditor/RichTextEditor';
import type {
  NoteAuthor,
  NoteBody,
} from '@backrs_ui/grow-ui/src/redux/coachingDashboard/coachingDashboard.types';
import { transcribeAudio } from '@backrs_ui/grow-ui/src/redux/coachLubav/transcribeAudio';
import type {
  ChipAttachment,
  EditorMode,
  RegistryEntry,
} from '@backrs_ui/grow-ui/src/redux/coachNoteEditor/coachNoteEditor';
import { isImageMime } from '@backrs_ui/grow-ui/src/utils/uploadFile';

import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  LinearProgress,
  MenuItem,
  Select,
  type SelectChangeEvent,
  Stack,
  SxProps,
  Theme,
  Typography,
} from '@mui/material';

import { formatNoteDate, formatNoteTimestamp } from './coachNotes.utils';
import { COACH_NOTE_TEMPLATES, type CoachNoteTemplate } from './coachNoteTemplates';
import { MAX_ATTACHMENTS, useNoteAttachments } from './useNoteAttachments';
import { CoachBadge } from '../CoachBadge';

const strings = appStrings.modules.coachingDashboard.coachNotes;
const recordingErrors = commonAppStrings.modules.audioRecording.errors;

const MAX_DICTATION_MS = 2 * 60 * 1000;

export type SaveStatus = 'idle' | 'saving' | 'saved';

type CoachNoteEditorModalProps = Readonly<{
  mode: EditorMode;
  body: NoteBody | null;
  chips: ChipAttachment[];
  saveStatus: SaveStatus;
  createdAt: string | null;
  lastSavedAt: string | null;
  author: NoteAuthor | null;
  onChangeBody: (body: NoteBody) => void;
  onAddChip: (chip: ChipAttachment) => void;
  onUpdateChip: (chip: ChipAttachment) => void;
  onRemoveChip: (localId: string) => void;
  onAddRegistryEntry: (entry: RegistryEntry) => void;
  onMinimize: () => void;
  onClose: () => void;
}>;

export const CoachNoteEditorModal = ({
  mode,
  body,
  chips,
  saveStatus,
  createdAt,
  lastSavedAt,
  author,
  onChangeBody,
  onAddChip,
  onUpdateChip,
  onRemoveChip,
  onAddRegistryEntry,
  onMinimize,
  onClose,
}: CoachNoteEditorModalProps) => {
  // Snapshot the body at mount so live edits don't re-hydrate the editor (which would reset the caret).
  const [editorInitialValue, setEditorInitialValue] = useState<NoteBody | null>(body);
  const [validationError, setValidationError] = useState<string | null>(null);
  // Stable key to reset the editor's internal state when applying a template.
  const editorKey = useRef(0);
  // Skip the first onChange after mount — it's the editor hydrating, not a user edit.
  const hasHydrated = useRef(false);
  // Last serialized body we propagated. The editor fires onChange on selection
  // changes too, so we ignore anything that leaves the content identical.
  const lastBodyJson = useRef<string | null>(null);

  const navigate = useNavigate();

  const editorControlRef = useRef<RichTextEditorHandle | null>(null);
  const imageInputRef = useRef<HTMLInputElement | null>(null);
  const attachInputRef = useRef<HTMLInputElement | null>(null);

  const attachments = useNoteAttachments({
    body,
    chips,
    onAddChip,
    onUpdateChip,
    onRemoveChip,
    onAddRegistryEntry,
  });

  const handleTranscript = useCallback((text: string) => {
    editorControlRef.current?.insertText(text);
  }, []);

  const handleRecordingError = useCallback((reason: AudioRecorderErrorReason) => {
    setValidationError(recordingErrors[reason]);
  }, []);

  const {
    status: dictationStatus,
    startRecording,
    stopRecording,
  } = useAudioRecorder({
    transcribe: transcribeAudio,
    onTranscript: handleTranscript,
    onError: handleRecordingError,
    maxDurationMs: MAX_DICTATION_MS,
  });

  const handleStartRecording = useCallback(() => {
    setValidationError(null);
    void startRecording();
  }, [startRecording]);

  // Applying a template rebuilds the editor, so it has to wait out the whole
  // dictation — a transcript arriving afterwards would land in the new content
  // instead of what was dictated into.
  const canApplyTemplate = !attachments.isUploading && dictationStatus === 'idle';

  const handleChange = useCallback(
    (next: NoteBody) => {
      const nextJson = JSON.stringify(next);

      if (!hasHydrated.current) {
        hasHydrated.current = true;
        lastBodyJson.current = nextJson;

        return;
      }

      if (nextJson === lastBodyJson.current) return;

      lastBodyJson.current = nextJson;
      onChangeBody(next);
    },
    [onChangeBody],
  );

  const handleSelectTemplate = useCallback(
    (template: CoachNoteTemplate) => {
      setEditorInitialValue(template.body);
      hasHydrated.current = false;
      editorKey.current += 1;
      onChangeBody(template.body);
    },
    [onChangeBody],
  );

  const handleTemplateChange = useCallback(
    (event: SelectChangeEvent<string>) => {
      // A focused Select still opens on keypress after it turns disabled, so the
      // guard has to live here rather than on the prop alone.
      if (!canApplyTemplate) return;

      const template = COACH_NOTE_TEMPLATES.find((t) => t.id === event.target.value);

      if (template) handleSelectTemplate(template);
    },
    [canApplyTemplate, handleSelectTemplate],
  );

  const handleOpenImagePicker = useCallback(() => {
    imageInputRef.current?.click();
  }, []);

  const handleOpenAttachPicker = useCallback(() => {
    attachInputRef.current?.click();
  }, []);

  const handleFileSelected = useCallback(
    (kind: 'image' | 'attach') => async (event: React.ChangeEvent<HTMLInputElement>) => {
      const file = event.target.files?.[0];

      event.target.value = '';
      if (!file) return;

      const isImage = isImageMime(file.type);

      if (kind === 'image' && !isImage) {
        setValidationError(strings.imageTypeError);

        return;
      }

      if (kind === 'attach' && isImage) {
        setValidationError(strings.attachNonImageError);

        return;
      }

      setValidationError(null);

      if (kind === 'image') {
        const result = await attachments.uploadImage(file);

        if (!result.ok) {
          setValidationError(
            result.reason === 'too_large' ? strings.fileTooLargeError : strings.uploadError,
          );

          return;
        }

        editorControlRef.current?.insertImage(result.src, result.alt);
      } else {
        const result = await attachments.uploadAttachment(file);

        if (!result.ok) {
          setValidationError(strings.fileTooLargeError);
        }
      }
    },
    [attachments],
  );

  // Clicking outside the modal minimizes it; the X, Done, and Escape close it.
  // Both unmount the editor, so hold teardown while an upload or a transcription
  // is in flight — each resolves by writing into state that is no longer there to
  // receive it.
  const isEditorBusy = attachments.isUploading || dictationStatus === 'transcribing';

  const handleDialogClose = useCallback(
    (_event: object, reason: 'backdropClick' | 'escapeKeyDown') => {
      if (isEditorBusy) return;

      // Unmounting drops the recording, and minimizing otherwise loses nothing, so
      // a stray click outside must not be the thing that bins a dictation. Closing
      // stays available — that one is deliberate.
      if (reason === 'backdropClick') {
        if (dictationStatus === 'recording') return;

        onMinimize();
      } else {
        onClose();
      }
    },
    [isEditorBusy, dictationStatus, onMinimize, onClose],
  );

  const title = mode === 'create' ? strings.editorTitleCreate : strings.editorTitleEdit;
  const createdDate = createdAt ? formatNoteDate(createdAt) : null;
  const lastModifiedLabel = lastSavedAt
    ? `${strings.lastModifiedLabel} ${formatNoteTimestamp(lastSavedAt)}`
    : null;
  const isSaving = saveStatus === 'saving';

  return (
    <Dialog
      open
      onClose={handleDialogClose}
      maxWidth="md"
      fullWidth
      sx={dialogSx}
      PaperProps={{ id: 'coach-note-editor-modal' }}
    >
      <DialogTitle sx={titleSx}>
        <Box sx={backgroundCirclesSx}>
          <BackgroundCircles />
        </Box>
        <Box sx={titleLeftSx}>
          <FeaturedIcon iconName="Edit04" size="lg" color="Colors/Foreground/fg-secondary" />
          <Box sx={titleTextGroupSx}>
            <Typography variant="text-md-semibold">{title}</Typography>
            <Typography variant="text-sm-regular" sx={subtitleSx}>
              {strings.editorSubtitle}
            </Typography>
            {(author || createdDate) && (
              <Box sx={authorRowSx}>
                {author && (
                  <CoachBadge
                    id="coach-note-author-badge"
                    firstName={author.first_name}
                    lastName={author.last_name}
                    profileImageUrl={author.profile_image_url}
                    onClick={() => navigate(`${APP_ROUTE.PROFILE}/${author.id}`)}
                  />
                )}
                {createdDate && (
                  <Typography variant="text-xs-regular" sx={metaTextSx}>
                    {createdDate}
                  </Typography>
                )}
              </Box>
            )}
          </Box>
        </Box>
        <Box sx={titleActionsSx}>
          <IconButton
            id="coach-note-editor-minimize-btn"
            aria-label={strings.editorMinimizeLabel}
            onClick={onMinimize}
            size="small"
            disabled={isEditorBusy || dictationStatus === 'recording'}
          >
            <AppIcon iconName="Minus" diameter="20px" color="Gray/600" />
          </IconButton>
          <IconButton
            id="coach-note-editor-close-btn"
            aria-label={strings.editorCloseLabel}
            onClick={onClose}
            size="small"
            disabled={isEditorBusy}
          >
            <AppIcon iconName="XClose" diameter="20px" color="Gray/600" />
          </IconButton>
        </Box>
      </DialogTitle>
      <DialogContent sx={contentSx}>
        <RichTextEditor
          key={editorKey.current}
          id="coach-note-editor"
          initialValue={editorInitialValue}
          onChange={handleChange}
          placeholder={strings.editorPlaceholder}
          onInsertImage={handleOpenImagePicker}
          onAttachFile={handleOpenAttachPicker}
          attachmentSlotsRemaining={attachments.slotsRemaining}
          dictation={{
            status: dictationStatus,
            onStart: handleStartRecording,
            onStop: stopRecording,
          }}
          controlRef={editorControlRef}
        />
        <input
          id="coach-note-image-input"
          ref={imageInputRef}
          type="file"
          accept="image/jpeg,image/png"
          hidden
          onChange={handleFileSelected('image')}
        />
        <input
          id="coach-note-attach-input"
          ref={attachInputRef}
          type="file"
          accept="application/pdf,application/vnd.openxmlformats-officedocument.wordprocessingml.document,text/plain"
          hidden
          onChange={handleFileSelected('attach')}
        />
        {(attachments.chips.length > 0 || attachments.slotsUsed > 0) && (
          <Box sx={attachmentRowSx}>
            {attachments.chips.length > 0 && (
              <Stack direction="row" sx={chipRowSx} flexWrap="wrap">
                {attachments.chips.map((chip) => (
                  <AttachedFileChip
                    key={chip.localId}
                    filename={chip.filename}
                    status={chip.status}
                    onRemove={() => attachments.removeChip(chip.localId)}
                  />
                ))}
              </Stack>
            )}
            {attachments.slotsUsed > 0 && (
              <Typography variant="text-xs-regular" sx={slotsHintSx}>
                {strings.attachmentSlotsHint(attachments.slotsUsed, MAX_ATTACHMENTS)}
              </Typography>
            )}
          </Box>
        )}
        {attachments.isUploading && <LinearProgress sx={progressSx} />}
        {validationError && (
          <Alert severity="error" onClose={() => setValidationError(null)} sx={errorAlertSx}>
            {validationError}
          </Alert>
        )}
        {lastModifiedLabel && (
          <Box sx={lastModifiedRowSx}>
            <Typography variant="text-xs-regular" sx={metaTextSx}>
              {lastModifiedLabel}
            </Typography>
            {isSaving && <CircularProgress size={12} sx={spinnerSx} />}
          </Box>
        )}
      </DialogContent>
      <DialogActions sx={actionsSx} disableSpacing>
        {mode === 'create' && (
          <Select
            id="coach-note-editor-template-select"
            displayEmpty
            size="small"
            value=""
            onChange={handleTemplateChange}
            disabled={!canApplyTemplate}
            MenuProps={TEMPLATE_MENU_PROPS}
            aria-label={strings.editorTemplateMenuLabel}
            renderValue={() => (
              <Box sx={templateRenderValueSx}>
                <Typography variant="text-md-medium" sx={templateRenderValueLabelSx}>
                  {strings.editorUseTemplate}
                </Typography>
                <Box sx={templateRenderValueIconSx}>
                  <AppIcon
                    iconName="ChevronDown"
                    diameter="20px"
                    color="Colors/Text/text-placeholder"
                  />
                </Box>
              </Box>
            )}
            sx={templateSelectSx}
          >
            {COACH_NOTE_TEMPLATES.map((template) => (
              <MenuItem
                key={template.id}
                id={`coach-note-editor-template-${template.id}`}
                value={template.id}
                disableRipple
              >
                <Typography variant="text-md-medium">{template.label}</Typography>
              </MenuItem>
            ))}
          </Select>
        )}
        <Button
          id="coach-note-editor-done-btn"
          hierarchy="Primary"
          size="lg"
          onClick={onClose}
          disabled={isEditorBusy}
        >
          {strings.editorDone}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

const dialogSx: SxProps<Theme> = ({ backrs }) => ({
  '& .MuiDialog-paper': {
    borderRadius: backrs.radius['radius-xl'],
    boxShadow: backrs.shadows['shadow-xl'],
    border: `1px solid ${backrs.colors['Colors/Base/Black']}`,
    overflow: 'hidden',
  },
});

const titleSx: SxProps<Theme> = ({ backrs }) => ({
  position: 'relative',
  display: 'flex',
  alignItems: 'flex-start',
  justifyContent: 'space-between',
  paddingInline: backrs.spacing['spacing-3xl'],
  paddingTop: backrs.spacing['spacing-3xl'],
  marginBottom: backrs.spacing['spacing-2xl'],
});

// Circles SVG is 336x336 with center at (168,168). Dialog title has
// px/pt of spacing-3xl (24px) and a lg FeaturedIcon is 48x48, so the icon
// center sits at (48, 48) from the DialogTitle origin. Offset the circles
// by -120 so their center (168 + -120 = 48) aligns with the icon center.
const backgroundCirclesSx: SxProps<Theme> = {
  position: 'absolute',
  top: -120,
  left: -120,
  zIndex: 0,
  pointerEvents: 'none',
};

const titleLeftSx: SxProps<Theme> = ({ backrs }) => ({
  position: 'relative',
  zIndex: 1,
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'flex-start',
  gap: backrs.spacing['spacing-lg'],
});

const titleTextGroupSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: backrs.spacing['spacing-xs'],
});

const subtitleSx: SxProps<Theme> = ({ backrs }) => ({
  color: backrs.colors['Colors/Text/text-tertiary'],
});

const metaTextSx: SxProps<Theme> = ({ backrs }) => ({
  color: backrs.colors['Gray/500'],
});

const authorRowSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  flexDirection: 'row',
  alignItems: 'center',
  gap: backrs.spacing['spacing-sm'],
  paddingTop: backrs.spacing['spacing-sm'],
});

const lastModifiedRowSx: SxProps<Theme> = ({ backrs }) => ({
  marginTop: backrs.spacing['spacing-sm'],
  display: 'flex',
  flexDirection: 'row',
  alignItems: 'center',
  gap: backrs.spacing['spacing-xs'],
});

const spinnerSx: SxProps<Theme> = ({ backrs }) => ({
  flexShrink: 0,
  color: backrs.colors['Gray/500'],
});

const titleActionsSx: SxProps<Theme> = ({ backrs }) => ({
  position: 'relative',
  zIndex: 1,
  display: 'flex',
  alignItems: 'center',
  gap: backrs.spacing['spacing-xs'],
});

const contentSx: SxProps<Theme> = {
  minHeight: '320px',
  display: 'flex',
  paddingBottom: 0,
  flexDirection: 'column',
  '& [data-testid="rich-text-editor"]': {
    flex: 1,
    minHeight: '280px',
  },
};

const actionsSx: SxProps<Theme> = ({ backrs, breakpoints }) => ({
  paddingInline: backrs.spacing['spacing-3xl'],
  paddingBottom: backrs.spacing['spacing-2xl'],
  gap: backrs.spacing['spacing-md'],
  [breakpoints.down('sm')]: {
    flexDirection: 'column',
    '& > *': {
      width: '100%',
    },
  },
});

const templateSelectSx: SxProps<Theme> = {
  marginRight: 'auto',
};

const templateRenderValueSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  gap: backrs.spacing['spacing-md'],
  boxSizing: 'border-box',
  width: '100%',
  marginRight: backrs.spacing['spacing-md'],
});

const templateRenderValueIconSx: SxProps<Theme> = {
  width: '20px',
  height: '20px',
};

const templateRenderValueLabelSx: SxProps<Theme> = ({ backrs }) => ({
  color: backrs.colors['Colors/Text/text-placeholder'],
  minWidth: 0,
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
});

const attachmentRowSx: SxProps<Theme> = ({ backrs }) => ({
  marginTop: backrs.spacing['spacing-md'],
  display: 'flex',
  flexDirection: 'row',
  alignItems: 'center',
  flexWrap: 'wrap',
  gap: backrs.spacing['spacing-md'],
});

const chipRowSx: SxProps<Theme> = ({ backrs }) => ({
  gap: backrs.spacing['spacing-xs'],
});

const progressSx: SxProps<Theme> = ({ backrs }) => ({
  marginTop: backrs.spacing['spacing-md'],
  backgroundColor: backrs.colors['Colors/Background/bg-brand-secondary'],
  '& .MuiLinearProgress-bar': {
    backgroundColor: backrs.colors['Colors/Background/bg-brand-solid'],
  },
});

const errorAlertSx: SxProps<Theme> = ({ backrs }) => ({
  marginTop: backrs.spacing['spacing-md'],
});

const slotsHintSx: SxProps<Theme> = ({ backrs }) => ({
  color: backrs.colors['Gray/500'],
});

const templateMenuPaperSx: SxProps<Theme> = ({ backrs }) => ({
  maxHeight: 360,
  mt: backrs.spacing['spacing-md'],
  boxShadow: backrs.shadows['shadow-xl'],
  border: `1px solid ${backrs.colors['Colors/Foreground/fg-primary']}`,
  borderRadius: backrs.radius['radius-md'],
});

const TEMPLATE_MENU_PROPS = {
  anchorOrigin: { vertical: 'bottom', horizontal: 'left' },
  transformOrigin: { vertical: 'top', horizontal: 'left' },
  PaperProps: { sx: templateMenuPaperSx },
} as const;
